import { useEffect, type RefObject } from "react";

/*
  Non-repeating foil animation using prime-ratio frequencies.
  Mouse tracking overrides when active; after 2s idle, smoothly
  transitions back to the oscillator orbit. background-size is
  600% 600%, so valid position range is ~17%–83% to avoid edges.
*/

const TAU = Math.PI * 2;
const SAFE_MIN = 17;
const SAFE_MAX = 83;
const SAFE_MID = (SAFE_MIN + SAFE_MAX) / 2;
const SAFE_RANGE = (SAFE_MAX - SAFE_MIN) / 2;
const IDLE_TIMEOUT = 2000;

const softClamp = (v: number, min: number, max: number) => {
  const mid = (min + max) / 2;
  const half = (max - min) / 2;
  return mid + half * Math.tanh((v - mid) / half);
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

// Oscillator config — prime periods ensure non-repeating path
const O = {
  x: [
    { freq: 1 / 7, amp: 0.8, phase: 0 },
    { freq: 1 / 17, amp: 0.5, phase: 1.3 },
    { freq: 1 / 29, amp: 0.3, phase: 2.7 },
  ],
  y: [
    { freq: 1 / 11, amp: 0.7, phase: 0.7 },
    { freq: 1 / 19, amp: 0.45, phase: 2.1 },
    { freq: 1 / 31, amp: 0.25, phase: 4.3 },
  ],
  glow: [
    { freq: 1 / 13, amp: 0.15 },
    { freq: 1 / 23, amp: 0.1 },
  ],
};

const oscillate = (
  waves: { freq: number; amp: number; phase: number }[],
  t: number,
  seed: number,
) =>
  waves.reduce(
    (sum, w) => sum + w.amp * Math.sin(TAU * w.freq * (t + seed) + w.phase),
    0,
  );

export function useFoilAnimation(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
  maxFps?: number,
) {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let animId = 0;
    const frameInterval = maxFps && maxFps > 0 ? 1000 / maxFps : 0;
    let lastPaintTime = -Infinity;
    const seed = {
      x: Math.random() * 1000,
      y: Math.random() * 1000,
      g: Math.random() * 1000,
    };

    const isDark = () => !document.documentElement.classList.contains("light");

    // Mouse state
    let mouseX = SAFE_MID;
    let mouseY = SAFE_MID;
    let lastMove = 0;
    let blend = 0; // 0 = mouse, 1 = oscillator

    const onMove = (e: MouseEvent) => {
      // Screen-space 0–1 mapped to button-sized displacement in gradient space
      const sx = e.clientX / window.innerWidth;
      const sy = e.clientY / window.innerHeight;
      const rect = el.getBoundingClientRect();
      const scaleX = rect.width / window.innerWidth;
      const scaleY = rect.height / window.innerHeight;
      mouseX = SAFE_MID + (sx - 0.5) * (SAFE_MAX - SAFE_MIN) * scaleX * -30.0;
      mouseY = SAFE_MID + (sy - 0.5) * (SAFE_MAX - SAFE_MIN) * scaleY * -20.0;
      lastMove = performance.now();
    };

    const tick = () => {
      const now = performance.now();
      if (now - lastPaintTime < frameInterval) {
        animId = requestAnimationFrame(tick);
        return;
      }
      lastPaintTime = now;
      const t = now / 1000;
      const idle = now - lastMove;

      // Blend: snap to mouse when active, ease to oscillator after timeout
      const targetBlend = idle > IDLE_TIMEOUT ? 1 : 0;
      const blendSpeed = targetBlend > blend ? 0.02 : 0.08; // slow transition to idle, fast snap to mouse
      blend = lerp(blend, targetBlend, blendSpeed);

      // Oscillator position (normalized -1..1, then mapped to safe range)
      const oscX = softClamp(
        SAFE_MID + oscillate(O.x, t, seed.x) * SAFE_RANGE,
        SAFE_MIN,
        SAFE_MAX,
      );
      const oscY = softClamp(
        SAFE_MID + oscillate(O.y, t, seed.y) * SAFE_RANGE,
        SAFE_MIN,
        SAFE_MAX,
      );

      const x = lerp(mouseX, oscX, blend);
      const y = lerp(mouseY, oscY, blend);

      const glow =
        0.15 +
        O.glow[0].amp *
          (0.5 + 0.5 * Math.sin(TAU * O.glow[0].freq * (t + seed.g))) +
        O.glow[1].amp *
          (0.5 + 0.5 * Math.cos(TAU * O.glow[1].freq * (t + seed.g) + 1.9));

      const a = isDark() ? 0.55 : 1;
      el.style.backgroundPosition = `${x}% ${y}%`;
      el.style.setProperty("--logo-foil-glow", String(glow * a));

      animId = requestAnimationFrame(tick);
    };

    // Start in oscillator mode
    blend = 1;
    animId = requestAnimationFrame(tick);
    el.addEventListener("pointermove", onMove);

    return () => {
      cancelAnimationFrame(animId);
      el.removeEventListener("pointermove", onMove);
    };
  }, [enabled, ref, maxFps]);
}
